import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Mandatory password change on first login for users flagged with
 * `must_change_password = true` (currently: guards imported from Asset IQ).
 *
 * Renders as a non-dismissable Dialog that overlays the whole app until the
 * password is successfully changed. The dialog can't be closed via ESC or
 * outside-click — the guard MUST rotate their temp password.
 */
export default function ForceChangePasswordGate() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const open = !!user && user.must_change_password === true;

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirmPwd) { toast.error("Passwords don't match"); return; }
    if (next === current) { toast.error("Pick a new password different from the temporary one"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — welcome to Ops IQ");
      setCurrent(""); setNext(""); setConfirmPwd("");
      // Refresh the AuthContext user so must_change_password flips to false.
      await refresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password change failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* not dismissable */ }}>
      <DialogContent
        className="max-w-md"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="force-change-password-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-brand" /> Set your Ops IQ password
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-900">
            Welcome, {user?.name}. Your account was imported from Asset IQ with a temporary password. Please choose a fresh password to continue.
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-slate-500">Temporary password (from Asset IQ)</Label>
            <Input
              type="password" required autoComplete="current-password"
              value={current} onChange={(e) => setCurrent(e.target.value)}
              placeholder="Enter the temp password shared by admin"
              className="h-11 mt-1"
              data-testid="fcp-current"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-slate-500">New password</Label>
            <Input
              type="password" required autoComplete="new-password"
              value={next} onChange={(e) => setNext(e.target.value)}
              placeholder="At least 6 characters"
              className="h-11 mt-1"
              data-testid="fcp-new"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-slate-500">Confirm new password</Label>
            <Input
              type="password" required autoComplete="new-password"
              value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
              placeholder="Type the new password again"
              className="h-11 mt-1"
              data-testid="fcp-confirm"
            />
          </div>
          <Button
            type="submit" disabled={busy}
            className="w-full h-11 bg-brand hover:bg-brand-dark text-white"
            data-testid="fcp-submit"
          >
            <KeyRound className="h-4 w-4 mr-2" />
            {busy ? "Updating…" : "Set new password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
